import React from "react";
import { format } from "date-fns";
import clsx from "clsx";
import { SkeletonLoader } from "@merge-api/merge-javascript-shared";
import { Trash, ChevronRight, Users } from "lucide-react";
import { Link } from "react-router-dom";
import {
  AuthType,
  LinkedAccount,
  UnreleasedAPICategory,
  PauseReason,
  UserType,
  APICategory,
} from "../../../models/Entities";
import { displayNameForAPICategoryIntegration } from "../../../models/Helpers";
import { getLinkedAccountDetailOverviewPathForId } from "../../../router/RouterUtils";
import IntegrationNameAndLogo from "../../shared/IntegrationNameAndLogo";
import EmptyStateWrapper from "../../shared/EmptyStateWrapper";
import MergeTable from "../../shared/MergeTable";
import useAppContext from "../../context/useAppContext";
import { Button, Badge, Tooltip, Text, ButtonVariant } from "@merge-api/merge-javascript-shared";
import LinkedAccountStatusBadge from "./shared/LinkedAccountStatusBadge";
import { conditionallyPluralize } from "../../../utils/general";

const LinkedAccountLink = (props: React.PropsWithChildren<{ id: string }>) => {
  return (
    <Link
      to={getLinkedAccountDetailOverviewPathForId(props.id)}
      className="block w-full text-black"
    >
      {props.children}
    </Link>
  );
};

type LinkedAccountTableProps = {
  forceResync: (linkedAccount: LinkedAccount) => void;
  isLoading: boolean;
  linkedAccounts?: LinkedAccount[];
  isTestAccounts: boolean;
  setLinkedAccountToDelete: (linkedAccount: LinkedAccount) => void;
};

function LinkedAccountTable(props: LinkedAccountTableProps) {
  const { isLoading, linkedAccounts, setLinkedAccountToDelete } = props;
  const { user } = useAppContext();
  const isSupportRole = user.type === UserType.support;

  const header = (
    <>
      <th scope="col">Date linked</th>
      <th scope="col">Organization</th>
      <th scope="col">Integration</th>
      <th scope="col">Category</th>
      <th scope="col">Status</th>
      <td scope="col"></td>
    </>
  );

  const linkedAccountDate = (linkedAccount: LinkedAccount) => {
    const linkDate = linkedAccount.completed_at;

    return <Text>{linkDate ? format(new Date(linkDate), "MMM dd, yyyy") : "-"}</Text>;
  };

  // This is an array containing all the rows for Linked Accounts if they exist
  const linkedAccountsRows =
    linkedAccounts &&
    linkedAccounts?.length > 0 &&
    linkedAccounts.map((linkedAccount) => (
      <React.Fragment key={linkedAccount.id}>
        <tr
          className={clsx(
            "table-link",
            linkedAccount.pause_reason == PauseReason.PLAN_LIMIT_REACHED && "text-gray-40",
          )}
          key={linkedAccount.id}
          style={{ borderBottomWidth: 0 }}
        >
          <td>
            <LinkedAccountLink id={linkedAccount.id}>
              {linkedAccountDate(linkedAccount)}
            </LinkedAccountLink>
          </td>
          <td data-fr-ignore>
            <LinkedAccountLink id={linkedAccount.id}>
              <div className="flex flex-row items-center gap-1.5">
                {linkedAccount?.is_duplicate && (
                  <Tooltip title="Duplicate Linked Account detected">
                    <Users size={16} className="text-yellow-70" />
                  </Tooltip>
                )}
                {/* Organization name is customer-chosen and is thus masked at the cell (Tooltip body is portal-mounted and covered by maskTextSelector) */}
                <Tooltip title={linkedAccount.end_user.organization_name}>
                  <div className="ph-no-capture max-w-[300px] truncate">
                    <Text>{linkedAccount.end_user.organization_name}</Text>
                  </div>
                </Tooltip>
              </div>
            </LinkedAccountLink>
          </td>
          <td>
            <LinkedAccountLink id={linkedAccount.id}>
              <div className="flex flex-row items-center gap-2">
                <IntegrationNameAndLogo integration={linkedAccount.integration} />
                {linkedAccount.auth_type === AuthType.SFTP && (
                  <Badge className="items-center gap-1" color="purple">
                    SFTP
                  </Badge>
                )}
              </div>
            </LinkedAccountLink>
          </td>
          <td>
            <LinkedAccountLink id={linkedAccount.id}>
              {displayNameForAPICategoryIntegration(
                linkedAccount.category as APICategory | UnreleasedAPICategory,
              )}
            </LinkedAccountLink>
          </td>
          <td>
            <LinkedAccountLink id={linkedAccount.id}>
              <div className="flex flex-row gap-x-1.5 items-center">
                <LinkedAccountStatusBadge linkedAccount={linkedAccount} />
                {linkedAccount.open_issue_count > 0 && (
                  <Badge color="red">
                    {conditionallyPluralize("issue", linkedAccount.open_issue_count)}
                  </Badge>
                )}
              </div>
            </LinkedAccountLink>
          </td>
          <td className="flex justify-end items-center gap-x-2">
            <Tooltip title="Delete linked account">
              <Button
                variant={ButtonVariant.IconOnly}
                size="sm"
                className={clsx("mr-2 max-w-7", isSupportRole && "opacity-50 cursor-not-allowed")}
                disabled={isSupportRole}
                onClick={(event: { stopPropagation: () => void }) => {
                  event.stopPropagation();
                  setLinkedAccountToDelete(linkedAccount);
                }}
              >
                <Trash size={16} />
              </Button>
            </Tooltip>

            <Tooltip title="Review linked account details">
              <LinkedAccountLink id={linkedAccount.id}>
                <ChevronRight size={16} className="text-gray-40" />
              </LinkedAccountLink>
            </Tooltip>
          </td>
        </tr>
      </React.Fragment>
    ));

  const content = (
    <>
      {!isLoading && linkedAccounts ? (
        linkedAccounts.length ? (
          linkedAccountsRows
        ) : (
          <tr>
            <td colSpan={6}>
              <EmptyStateWrapper isTable title="No Linked Accounts" />
            </td>
          </tr>
        )
      ) : (
        Array.from({ length: 25 }).map((_, i) => (
          <tr key={`linked-account-skel-row-${i}`} className="table-row">
            <td colSpan={6}>
              <SkeletonLoader height={32} borderRadius={8} fullWidth />
            </td>
          </tr>
        ))
      )}
    </>
  );

  return <MergeTable header={header} content={content} hasMorePaddingOnFirstElement />;
}

export default LinkedAccountTable;
